import React, { useState, useEffect } from 'react';
import {
    Box,
    Button,
    TextField,
    Typography,
    Select,
    MenuItem,
    FormControl,
    InputLabel,
    IconButton,
    List,
    ListItem,
    ListItemButton,
    ListItemText,
    Checkbox,
    FormControlLabel,
    Grid,
    Paper,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions
} from '@mui/material';
import { Add, Delete, Save, Close } from '@mui/icons-material';
import styles from './ActionBuilder.module.scss';

const HTTP_METHODS = ['GET', 'POST', 'PUT', 'DELETE', 'PATCH'];
const PARAM_LOCATIONS = ['query', 'path', 'header', 'body'];
const PARAM_TYPES = ['string', 'integer', 'number', 'boolean'];

const ActionBuilder = ({ actions, onChange, readOnly = false }) => {
    const [selectedActionIndex, setSelectedActionIndex] = useState(null);
    const [editingAction, setEditingAction] = useState(null);

    // Initialize editing state when an action is selected
    useEffect(() => {
        if (selectedActionIndex !== null && actions[selectedActionIndex]) {
            setEditingAction({ ...actions[selectedActionIndex] });
        } else {
            setEditingAction(null);
        }
    }, [selectedActionIndex, actions]);

    const handleAddAction = () => {
        const newAction = {
            name: 'new_action',
            description: '',
            method: 'GET',
            path: '/',
            parameters: [] // Array of param objects { name, type, location, required, description }
        };
        const newActions = [...actions, newAction];
        onChange(newActions);
        setSelectedActionIndex(newActions.length - 1);
    };

    const handleDeleteAction = (index, e) => {
        e.stopPropagation();
        const newActions = actions.filter((_, i) => i !== index);
        onChange(newActions);
        if (selectedActionIndex === index) {
            setSelectedActionIndex(null);
        } else if (selectedActionIndex > index) {
            setSelectedActionIndex(selectedActionIndex - 1);
        }
    };

    const handleActionChange = (field, value) => {
        if (!editingAction) return;
        setEditingAction(prev => ({ ...prev, [field]: value }));
    };

    const handleCloseDialog = () => {
        setSelectedActionIndex(null);
    };

    const handleSaveAction = () => {
        if (selectedActionIndex === null || !editingAction) return;
        const newActions = [...actions];
        newActions[selectedActionIndex] = editingAction;
        onChange(newActions);
        handleCloseDialog();
    };

    const handleAddParameter = () => {
        if (!editingAction) return;
        const newParam = {
            name: 'new_param',
            type: 'string',
            location: 'query',
            required: false,
            description: ''
        };
        const updatedParams = [...(Array.isArray(editingAction.parameters) ? editingAction.parameters : []), newParam];
        setEditingAction(prev => ({ ...prev, parameters: updatedParams }));
    };

    const handleParameterChange = (index, field, value) => {
        if (!editingAction) return;
        const updatedParams = [...(Array.isArray(editingAction.parameters) ? editingAction.parameters : [])];
        const oldParam = updatedParams[index];
        updatedParams[index] = { ...oldParam, [field]: value };

        let newPath = editingAction.path;

        // Auto-update path if parameter location changes to 'path', or its name changes while it is already a 'path' parameter
        if (field === 'location' && value === 'path') {
            const paramName = oldParam.name;
            if (paramName && !newPath.includes(`{${paramName}}`)) {
                newPath = newPath.endsWith('/') ? `${newPath}{${paramName}}` : `${newPath}/{${paramName}}`;
            }
        } else if (field === 'name' && oldParam.location === 'path') {
            const oldName = oldParam.name;
            const newName = value;
            if (oldName && newPath.includes(`{${oldName}}`)) {
                newPath = newPath.replace(`{${oldName}}`, `{${newName}}`);
            } else if (newName && !newPath.includes(`{${newName}}`)) {
                newPath = newPath.endsWith('/') ? `${newPath}{${newName}}` : `${newPath}/{${newName}}`;
            }
        }

        setEditingAction(prev => ({
            ...prev,
            parameters: updatedParams,
            path: newPath
        }));
    };

    const handleDeleteParameter = (index) => {
        if (!editingAction) return;
        const updatedParams = (Array.isArray(editingAction.parameters) ? editingAction.parameters : []).filter((_, i) => i !== index);
        setEditingAction(prev => ({ ...prev, parameters: updatedParams }));
    };

    return (
        <Box className={styles.container} sx={{ width: '100%', flexGrow: 1 }}>
            <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                <Box sx={{ mb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography variant="subtitle1" fontWeight="bold">
                        {readOnly ? 'Tools' : 'Actions'}
                    </Typography>
                    {!readOnly && (
                        <Button startIcon={<Add />} size="small" variant="contained" onClick={handleAddAction}>
                            Add
                        </Button>
                    )}
                </Box>
                <Paper variant="outlined" sx={{ flexGrow: 1, overflow: 'auto' }}>
                    <List dense>
                        {actions.map((action, index) => (
                            <ListItem
                                key={index}
                                disablePadding
                                secondaryAction={
                                    !readOnly ? (
                                        <IconButton edge="end" size="small" onClick={(e) => handleDeleteAction(index, e)}>
                                            <Delete fontSize="small" />
                                        </IconButton>
                                    ) : undefined
                                }
                                sx={{
                                    borderBottom: '1px solid',
                                    borderColor: 'divider',
                                }}
                            >
                                <ListItemButton
                                    selected={selectedActionIndex === index}
                                    onClick={() => setSelectedActionIndex(index)}
                                    sx={{
                                        '&.Mui-selected': { bgcolor: 'action.selected' }
                                    }}
                                >
                                    <ListItemText
                                        primary={action.name}
                                        secondary={readOnly
                                            ? <Typography variant="body2" color="text.secondary" component="span">
                                                Parameters:{' '}
                                                {Array.isArray(action.parameters) && action.parameters.length > 0
                                                    ? action.parameters.map((p, i) => (
                                                        <React.Fragment key={p.name}>
                                                            {i > 0 && ', '}
                                                            <Box component="span" fontWeight={p.required ? 'bold' : 'normal'} sx={{ color: p.required ? 'text.primary' : 'inherit' }}>
                                                                {p.name}
                                                            </Box>
                                                        </React.Fragment>
                                                    ))
                                                    : 'None'}
                                            </Typography>
                                            : `${action.method} ${action.path}`
                                        }
                                        primaryTypographyProps={{ fontWeight: 500 }}
                                    />
                                </ListItemButton>
                            </ListItem>
                        ))}
                        {actions.length === 0 && (
                            <Box sx={{ p: 2, textAlign: 'center', color: 'text.secondary' }}>
                                {readOnly ? 'Connect to the MCP server to fetch available tools/actions.' : 'No actions defined. Click Add to create one.'}
                            </Box>
                        )}
                    </List>
                </Paper>
            </Box>

            {/* Dialog: Action Details Editor */}
            <Dialog
                open={editingAction !== null}
                onClose={handleCloseDialog}
                maxWidth="md"
                fullWidth
                PaperProps={{ sx: { bgcolor: 'background.paper', backgroundImage: 'none' } }}
            >
                {editingAction && (
                    <>
                        <DialogTitle sx={{ m: 0, p: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <Typography variant="h6" component="span">{readOnly ? 'Tool Details' : 'Edit Action Configuration'}</Typography>
                            <IconButton aria-label="close" onClick={handleCloseDialog} sx={{ color: (theme) => theme.palette.grey[500] }}>
                                <Close />
                            </IconButton>
                        </DialogTitle>
                        <DialogContent dividers sx={{ p: { xs: 2, md: 3 } }}>
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                                <Paper variant="outlined" sx={{ p: 2 }}>
                                    <Typography variant="subtitle2" sx={{ mb: 2, color: 'primary.main' }}>
                                        {readOnly ? 'Tool Configuration' : 'Action Configuration'}
                                    </Typography>
                                    <Grid container spacing={2}>
                                        <Grid size={{ xs: 12, sm: 6 }}>
                                            <TextField
                                                fullWidth
                                                label="Name"
                                                size="small"
                                                value={editingAction.name}
                                                onChange={(e) => handleActionChange('name', e.target.value)}
                                                helperText="Unique identifier for this action"
                                                InputProps={{ readOnly }}
                                            />
                                        </Grid>
                                        <Grid size={{ xs: 12, sm: 6 }}>
                                            <FormControl fullWidth size="small">
                                                <InputLabel>Method</InputLabel>
                                                <Select
                                                    value={editingAction.method}
                                                    label="Method"
                                                    onChange={(e) => handleActionChange('method', e.target.value)}
                                                    disabled={readOnly}
                                                >
                                                    {HTTP_METHODS.map(m => <MenuItem key={m} value={m}>{m}</MenuItem>)}
                                                </Select>
                                            </FormControl>
                                        </Grid>
                                        <Grid size={12}>
                                            <TextField
                                                fullWidth
                                                label="Path"
                                                size="small"
                                                value={editingAction.path}
                                                onChange={(e) => handleActionChange('path', e.target.value)}
                                                helperText="e.g. /users/{id}"
                                                InputProps={{ readOnly }}
                                            />
                                        </Grid>
                                        <Grid size={12}>
                                            <TextField
                                                fullWidth
                                                label="Description"
                                                size="small"
                                                multiline
                                                rows={2}
                                                value={editingAction.description}
                                                onChange={(e) => handleActionChange('description', e.target.value)}
                                                InputProps={{ readOnly }}
                                            />
                                        </Grid>
                                        <Grid size={12}>
                                            <FormControlLabel
                                                control={
                                                    <Checkbox
                                                        checked={Boolean(editingAction.send_attachment)}
                                                        onChange={(e) => handleActionChange('send_attachment', e.target.checked)}
                                                        size="small"
                                                        disabled={readOnly}
                                                    />
                                                }
                                                label={
                                                    <Box>
                                                        <Typography variant="body2">Send Attachment</Typography>
                                                        <Typography variant="caption" color="text.secondary">
                                                            Forward the request's uploaded file as multipart form-data with this action
                                                        </Typography>
                                                    </Box>
                                                }
                                                sx={{ alignItems: 'flex-start' }}
                                            />
                                        </Grid>
                                        {editingAction.send_attachment && (
                                            <>
                                                <Grid size={{ xs: 12, sm: 6 }}>
                                                    <TextField
                                                        fullWidth
                                                        label="Attachment Content Field"
                                                        size="small"
                                                        value={editingAction.attachment_content_field || ''}
                                                        onChange={(e) => handleActionChange('attachment_content_field', e.target.value)}
                                                        helperText="Param holding the file content/base64 (optional)"
                                                        InputProps={{ readOnly }}
                                                    />
                                                </Grid>
                                                <Grid size={{ xs: 12, sm: 6 }}>
                                                    <TextField
                                                        fullWidth
                                                        label="Attachment Name Field"
                                                        size="small"
                                                        value={editingAction.attachment_name_field || ''}
                                                        onChange={(e) => handleActionChange('attachment_name_field', e.target.value)}
                                                        helperText="Param holding the file name (optional)"
                                                        InputProps={{ readOnly }}
                                                    />
                                                </Grid>
                                                <Grid size={{ xs: 12, sm: 6 }}>
                                                    <TextField
                                                        fullWidth
                                                        label="Attachment File Type"
                                                        size="small"
                                                        value={editingAction.attachment_file_type || ''}
                                                        onChange={(e) => handleActionChange('attachment_file_type', e.target.value)}
                                                        helperText="MIME/content type, e.g. application/pdf (optional)"
                                                        InputProps={{ readOnly }}
                                                    />
                                                </Grid>
                                                <Grid size={{ xs: 12, sm: 6 }}>
                                                    <TextField
                                                        fullWidth
                                                        label="Attachment Folder ID"
                                                        size="small"
                                                        value={editingAction.attachment_folder_id || ''}
                                                        onChange={(e) => handleActionChange('attachment_folder_id', e.target.value)}
                                                        helperText="Destination folder id (optional)"
                                                        InputProps={{ readOnly }}
                                                    />
                                                </Grid>
                                            </>
                                        )}
                                    </Grid>
                                </Paper>

                                <Paper variant="outlined" sx={{ p: 2 }}>
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                                        <Typography variant="subtitle2" sx={{ color: 'primary.main' }}>
                                            Parameters
                                        </Typography>
                                        {!readOnly && (
                                            <Button startIcon={<Add />} size="small" onClick={handleAddParameter}>
                                                Add Param
                                            </Button>
                                        )}
                                    </Box>

                                    {(!Array.isArray(editingAction.parameters) || editingAction.parameters.length === 0) && (
                                        <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                                            No parameters defined.
                                        </Typography>
                                    )}

                                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                                        {(Array.isArray(editingAction.parameters) ? editingAction.parameters : []).map((param, idx) => (
                                            <Paper key={idx} variant="outlined" sx={{ p: 1.5, bgcolor: 'background.default' }}>
                                                <Grid container spacing={1} alignItems="center">
                                                    <Grid size={{ xs: 12, sm: 3 }}>
                                                        <TextField
                                                            fullWidth
                                                            label="Name"
                                                            size="small"
                                                            value={param.name}
                                                            onChange={(e) => handleParameterChange(idx, 'name', e.target.value)}
                                                            InputProps={{ readOnly }}
                                                        />
                                                    </Grid>
                                                    <Grid size={{ xs: 6, sm: 2 }} sx={{ minWidth: '100px' }}>
                                                        <FormControl fullWidth size="small">
                                                            <InputLabel>Type</InputLabel>
                                                            <Select
                                                                value={param.type}
                                                                label="Type"
                                                                onChange={(e) => handleParameterChange(idx, 'type', e.target.value)}
                                                                disabled={readOnly}
                                                            >
                                                                {PARAM_TYPES.map(t => <MenuItem key={t} value={t}>{t}</MenuItem>)}
                                                            </Select>
                                                        </FormControl>
                                                    </Grid>
                                                    <Grid size={{ xs: 6, sm: 2 }} sx={{ minWidth: '100px' }}>
                                                        <FormControl fullWidth size="small">
                                                            <InputLabel>Location</InputLabel>
                                                            <Select
                                                                value={param.location}
                                                                label="Location"
                                                                onChange={(e) => handleParameterChange(idx, 'location', e.target.value)}
                                                                disabled={readOnly}
                                                            >
                                                                {PARAM_LOCATIONS.map(l => <MenuItem key={l} value={l}>{l}</MenuItem>)}
                                                            </Select>
                                                        </FormControl>
                                                    </Grid>
                                                    <Grid size={{ xs: 12, sm: 3 }}>
                                                        <TextField
                                                            fullWidth
                                                            label="Description"
                                                            size="small"
                                                            value={param.description || ''}
                                                            onChange={(e) => handleParameterChange(idx, 'description', e.target.value)}
                                                            InputProps={{ readOnly }}
                                                        />
                                                    </Grid>
                                                    <Grid size="auto">
                                                        <FormControlLabel
                                                            control={
                                                                <Checkbox
                                                                    checked={param.required}
                                                                    onChange={(e) => handleParameterChange(idx, 'required', e.target.checked)}
                                                                    size="small"
                                                                    disabled={readOnly}
                                                                />
                                                            }
                                                            label={<Typography variant="caption">Req</Typography>}
                                                        />
                                                    </Grid>
                                                    {!readOnly && (
                                                        <Grid size="auto">
                                                            <IconButton size="small" color="error" onClick={() => handleDeleteParameter(idx)}>
                                                                <Delete fontSize="small" />
                                                            </IconButton>
                                                        </Grid>
                                                    )}
                                                </Grid>
                                            </Paper>
                                        ))}
                                    </Box>
                                </Paper>
                            </Box>
                        </DialogContent>
                        <DialogActions sx={{ p: 2 }}>
                            <Button onClick={handleCloseDialog} color="inherit">
                                {readOnly ? 'Close' : 'Cancel'}
                            </Button>
                            {!readOnly && (
                                <Button
                                    variant="contained"
                                    startIcon={<Save />}
                                    onClick={handleSaveAction}
                                >
                                    Apply Changes
                                </Button>
                            )}
                        </DialogActions>
                    </>
                )}
            </Dialog>
        </Box>
    );
};

export default ActionBuilder;
